import { Box, Button, Group, Stack, Text } from '@mantine/core';
import { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { Bar, BarChart, CartesianGrid, ResponsiveContainer, Tooltip as RechartsTootip, XAxis, YAxis } from 'recharts';
import { useChartDatas, useBot, generateCSV, isSameDay } from '@mygpt/app';
import CustomContainer from '@/components/CustomContainer';
import CustomContainerHeader from '@/components/CustomContainer/CustomContainerHeader';
import CustomTooltip from '@/components/Dashboard/Chart/components/CustomTooltip';
import ChartTable from '@/components/Dashboard/ChartTable';
import { SvgFileDownload } from '@/components/Dashboard/icon';
import TitleCard from '@/components/Dashboard/TitleCard';

const ChatSatisfaction = () => {
  const { t } = useTranslation();
  const { botId } = useBot();
  const { datas, times, timesText } = useChartDatas();

  const totalRatedGood = useMemo(() => {
    let total = 0;
    datas.map((data) => {
      total += data.like_count;
    });
    return total;
  }, [datas]);

  const totalRatedBad = useMemo(() => {
    let total = 0;
    datas.map((data) => {
      total += data.dislike_count;
    });
    return total;
  }, [datas]);

  const data = useMemo(() => {
    const chartData: { name: string; good: number; bad: number }[] = [];
    times.map((item) => {
      let good = 0;
      let bad = 0;
      datas.map((data) => {
        if (isSameDay(item, data.updated_at)) {
          good += data.like_count;
          bad += data.dislike_count;
        }
      });

      const obj = {
        name: item,
        good,
        bad,
      };
      chartData.push(obj);
    });
    return chartData;
  }, [datas, times]);

  const tableData = useMemo(() => {
    const goodValue = times.map((time) => {
      let goodSum = 0;
      datas.map((data) => {
        if (isSameDay(time, data.updated_at)) goodSum += data.like_count;
      });
      return {
        key: time,
        value: goodSum,
      };
    });

    const badValue = times.map((time) => {
      let badSum = 0;
      datas.map((data) => {
        if (isSameDay(time, data.updated_at)) badSum += data.dislike_count;
      });
      return {
        key: time,
        value: badSum,
      };
    });

    return [
      {
        type: t('dashboard.card.rated_good'),
        value: goodValue,
      },
      {
        type: t('dashboard.card.rated_bad'),
        value: badValue,
      },
    ];
  }, [datas]);

  const DownloadButton = () => {
    const download = () => {
      const fileName = `GBase_${botId}_${t('dashboard.card.chat_satisfaction')}`;
      const header = [t('dashboard.series'), ...times, t('dashboard.total')];
      // 将每个数据对象转换为CSV格式的一行
      const rows = tableData.map((item) => {
        const rowsData = item.value;
        let total = 0;
        const everyCol = rowsData.map((row) => {
          total += row.value;
          return String(row.value);
        });
        return [item.type, ...everyCol, String(total)];
      });

      const data = {
        header,
        rows,
      };
      generateCSV(fileName, data);
    };

    return (
      <Button size="sm" variant="subtle" leftSection={<SvgFileDownload />} onClick={download}>
        <Text style={{ color: '#7C68FB' }}>{t('dashboard.export_csv')}</Text>
      </Button>
    );
  };

  return (
    <>
      <CustomContainerHeader
        title={t('dashboard.card.chat_satisfaction')}
        label={t('dashboard.card.chat_satisfaction_tip')}
      />
      <CustomContainer>
        <Stack gap={50}>
          <Group>
            <TitleCard
              title={t('dashboard.card.provide_feedback')}
              valueColor="#2CD9C5"
              value={totalRatedGood}
              desc={timesText}
            />
            <TitleCard
              title={t('dashboard.card.negative_feedback')}
              valueColor="#FF6C40"
              value={totalRatedBad}
              desc={timesText}
            />
          </Group>

          <Box w={'100%'} h={350} className="text-sm text-[#667085] font-light">
            <ResponsiveContainer width="100%" height="100%">
              <BarChart
                width={500}
                height={300}
                data={data}
                barGap={48}
                barCategoryGap={48}
                margin={{
                  top: 5,
                  right: 30,
                  left: 20,
                  bottom: 5,
                }}
              >
                <CartesianGrid stroke="#E4E7EC" strokeDasharray="0 0" vertical={false} />
                <XAxis axisLine={false} tickLine={false} tickMargin={10} padding={{ left: 45 }} dataKey="name" />
                <YAxis axisLine={false} tickLine={false} tickMargin={10} />
                <RechartsTootip
                  content={
                    <CustomTooltip
                      text={{ good: t('dashboard.card.rated_good'), bad: t('dashboard.card.rated_bad') }}
                    />
                  }
                />
                <Bar dataKey="bad" fill="#FF6C40" />
                <Bar dataKey="good" fill="#2CD9C5" />
              </BarChart>
            </ResponsiveContainer>
          </Box>
          <ChartTable
            title={t('dashboard.table.chat_satisfaction_title')}
            titleRightSection={<DownloadButton />}
            times={times}
            data={tableData}
          />
        </Stack>
      </CustomContainer>
    </>
  );
};

export default ChatSatisfaction;
